import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router';
import { RoutedComponentProps } from 'src/app-routes';
import { Dispatch } from 'src/components-bl/types';
import { Button } from 'src/components-dummy';
import { GalleryType, ShoppableBanner } from 'src/services';
import { IApiKey } from 'src/services/src/service/types/shops/api-keys';
import { editGalleryPageActions } from '../../Actions';
import { BannerPreview } from './components';
import {
  EditCustomInspirationsGalleryPageStyled,
  PreviewFooter,
  PreviewLayout,
  SideBarLayout,
  OverlayModalStyled,
} from '../../CustomInspirations/EditCustomInspirationsGalleryPage/EditCustomInspirationsGalleryPage.styles';
import { GalleryMapper } from './mapper';
import { ShoppableBannerDraft } from './types';
import { ExpandableSection } from '../../CustomInspirations/hooks/useExpandableOptions';
import { getQueryStringParams } from '../../CustomInspirations/common/utils';
import {
  Action,
  DirtyFormConfirmationDialog,
} from '../../CustomInspirations/components/DirtyFormConfirmationDialog/DirtyFormConfirmationDialog';
import { EditBannerSideBar } from './components/EditBannerSideBar/EditBannerSideBar';
import { useEditGalleryPage } from '../../useEditGalleryPage';
import { useValidateSchema } from 'src/hooks';
import { bannerValidationSchema } from './components/EditBannerSideBar/constants';

interface EditShoppableBannerPageProps extends RoutedComponentProps {
  shopId: number;
  dispatch: Dispatch;
  galleryId: string;
  gallery?: ShoppableBanner;
  hasDraft: boolean;
  shopApiKey?: IApiKey;
}

export function EditShoppableBannerPage({
  shopId,
  dispatch,
  galleryId,
  gallery,
  hasDraft,
  shopApiKey,
  permittedRouteMap,
}: EditShoppableBannerPageProps): JSX.Element {
  const mappedInitialGallery = useMemo(() => GalleryMapper.mapGalleryToDraft(gallery), [gallery]);
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  const [draftGallery, setDraftGallery] = useState<ShoppableBannerDraft | undefined>(
    mappedInitialGallery
  );

  const { errors, isValid, validate } = useValidateSchema<
    Pick<ShoppableBannerDraft, 'bannerLink' | 'bannerTextLink'>
  >({
    initialData: draftGallery,
    schema: bannerValidationSchema,
    validateOnStart: true,
  });

  const onSaveGallery = useCallback(
    async galleryToSave => {
      let updatedGallery;

      try {
        setIsSaveInProgress(true);

        updatedGallery = await (
          dispatch(
            editGalleryPageActions.updateGallery({
              shopId,
              galleryId,
              payload: galleryToSave,
            })
          ) as any
        ).unwrap();
      } catch (error) {
        console.error(error);
      }

      setIsSaveInProgress(false);

      if (updatedGallery) {
        return GalleryMapper.mapGalleryToDraft(updatedGallery);
      }
    },
    [dispatch, shopId, galleryId, setDraftGallery]
  );

  const {
    navigateToList,
    resetDraftAndCallback,
    onSaveDraftGallery,
    startTagsDetection,
    saveDraftAndCallback,
    applyAllSuggestedTags,
    removeAllSuggestedTags,
    triggerTheModalIfDirtyOrCallback,
    showDirtyModal,
    currentActionConfig,
    closeModal,
    isDirty,
  } = useEditGalleryPage({
    shopId,
    dispatch,
    galleryId,
    mappedInitialGallery,
    permittedRouteMap,
    onSaveGallery,
    draftGallery,
    setDraftGallery,
  });

  const onChange = useCallback(
    (partialGallery: Partial<ShoppableBannerDraft>) => {
      if (draftGallery) {
        setDraftGallery({ ...draftGallery, ...partialGallery });
      }
    },
    [draftGallery, setDraftGallery]
  );

  const navigateToImageSettings = useCallback(
    (imageId: string, openedSections: ExpandableSection[]) => {
      if (!permittedRouteMap.tagImageInShoppableBanner) {
        return;
      }

      const baseUrl = generatePath(permittedRouteMap.tagImageInShoppableBanner.path, {
        shopId,
        galleryId,
        imageId: imageId,
      });

      const queryParams = getQueryStringParams(openedSections);

      const targetUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

      dispatch(
        editGalleryPageActions.navigateTo({
          navigateTo: targetUrl,
        })
      );
    },
    [dispatch, permittedRouteMap.tagImageInShoppableBanner, galleryId, shopId]
  );

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await (
          dispatch(
            editGalleryPageActions.getGallery({
              shopId,
              galleryId,
              galleryType: GalleryType.ShoppableBanner,
            })
          ) as any
        ).unwrap();
        if (response) {
          setDraftGallery(GalleryMapper.mapGalleryToDraft(response));
        }
      } catch (error) {
        console.error(error);
        navigateToList();
      }
    };

    fetchGallery();
    dispatch(editGalleryPageActions.getApiKeys({ shopId }));

    return () => {
      dispatch(editGalleryPageActions.resetCurrentGallery());
      dispatch(editGalleryPageActions.resetApiKeys());
    };
  }, [shopId, galleryId, dispatch, navigateToList]);

  const resetImagesToInitialState = useCallback(() => {
    if (gallery && gallery.images) {
      const initialImages = gallery.images.map(image => ({ ...image }));
      setDraftGallery(prevDraft => ({
        ...prevDraft,
        images: initialImages,
      }));
    }
  }, [gallery, setDraftGallery]);

  useEffect(() => {
    resetImagesToInitialState();
  }, [gallery, resetImagesToInitialState]);

  const canSubmit = isValid && isDirty;

  return (
    <OverlayModalStyled show fullscreen={!hasDraft}>
      <EditCustomInspirationsGalleryPageStyled>
        <SideBarLayout>
          <EditBannerSideBar
            gallery={draftGallery}
            onChange={onChange}
            galleryId={galleryId}
            dispatch={dispatch}
            navigateToList={navigateToList}
            errors={errors}
            validate={validate}
          />
        </SideBarLayout>
        <PreviewLayout>
          <BannerPreview
            gallery={draftGallery}
            galleryId={galleryId}
            onChange={onChange}
            navigateToImageSettings={navigateToImageSettings}
            dispatch={dispatch}
            shopId={shopId}
            shopApiKey={shopApiKey}
            startTagsDetection={() =>
              triggerTheModalIfDirtyOrCallback(startTagsDetection, Action.Generate)
            }
            applyAllSuggestedTags={() =>
              triggerTheModalIfDirtyOrCallback(applyAllSuggestedTags, Action.AcceptTags)
            }
            removeAllSuggestedTags={() =>
              triggerTheModalIfDirtyOrCallback(removeAllSuggestedTags, Action.RejectTags)
            }
          />
          <PreviewFooter>
            <Button variant='secondary' onClick={navigateToList}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={onSaveDraftGallery}
              disabled={!canSubmit}
              loading={isSaveInProgress}
            >
              Save Banner
            </Button>
          </PreviewFooter>
        </PreviewLayout>
      </EditCustomInspirationsGalleryPageStyled>
      <DirtyFormConfirmationDialog
        action={currentActionConfig?.action}
        close={closeModal}
        isOpened={showDirtyModal}
        isLoading={isSaveInProgress}
        onResetDraftAndProceed={resetDraftAndCallback}
        onSaveAndProceed={saveDraftAndCallback}
      />
    </OverlayModalStyled>
  );
}
